import React, { useState, useEffect, useRef } from "react";
import { auth, db, storage } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  orderBy,
  doc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import DetalleIncidencia from "./DetalleIncidencia";
import Loader from "../components/Loader";
import { devLog } from "../utils/devLog";
import { tolerantId } from "../utils/firestoreHelpers";
import { leerFichaResidente } from "../utils/fichaResidente";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity, useIsReadOnly } from "../context/EffectiveIdentityContext";
import { useToast } from "../components/Toast";
import useIsDemo from "../hooks/useIsDemo";
import { HABI_API_BASE_URL, getAuthHeaders } from "../utils/habiApi";
import {
  FaClipboardList,
  FaUser,
  FaBuilding,
  FaCamera,
  FaPaperPlane,
  FaPaperclip,
  FaCheckCircle,
  FaMicrophone,
  FaChevronRight,
  FaTools,
  FaRegCommentDots,
  FaWrench,
  FaBolt,
  FaShieldAlt,
  FaBroom,
  FaFire,
  FaHardHat,
} from "react-icons/fa";
import styles from "./Incidencias.module.css";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  AppButton,
  Badge,
  SegmentedControl,
  AppEmptyState,
  SectionLabel,
  IconBox,
} from "../components/app";
import { appColors, appSpacing, appType } from "../utils/appTheme";
import { useTranslation } from "react-i18next";
import { formatDateLocalized } from "../utils/formatters";
import { getLocaleTag } from "../i18n";

// Icono por tema (categoría) para la caja de icono de cada card mobile.
// Solo presentación: mapea el string `issue` a un icono; fallback FaTools.
const ISSUE_ICONS = {
  Sanitario: FaWrench,
  Ascensor: FaTools,
  Electricidad: FaBolt,
  Seguridad: FaShieldAlt,
  Limpieza: FaBroom,
  Estructural: FaHardHat,
  Gas: FaFire,
  Otros: FaTools,
};

// Extrae título, descripción, ubicación, tema, tipo y prioridad desde un texto (transcripción)
function extractIncidenciaFromTranscript(text) {
  if (!text || typeof text !== "string") text = "";
  const t = text.trim();
  const lower = t.toLowerCase();

  const result = {
    titulo: "",
    descripcion: t,
    ubicacion: "",
    issue: "",
    type: "Personal",
    prioridad: "media",
  };

  // Prioridad — ampliado con variaciones regionales
  if (/\b(prioridad\s+)?alta\b|\burgente\b|\bcr[íi]tico\b|\bcr[íi]tica\b|\bemergencia\b|\bgrave\b|\binmediato\b|\bpeligro\b|\bpeligroso\b|\briesgo\b/.test(lower)) {
    result.prioridad = "alta";
  } else if (/\b(prioridad\s+)?baja\b|\bpoco\s+urgente\b|\bno\s+urgente\b|\bmenor\b|\bno\s+(es\s+)?grave\b|\bcuando\s+puedan\b|\bsin\s+apuro\b/.test(lower)) {
    result.prioridad = "baja";
  }

  // Tema (issue) — ampliado con variaciones regionales (AR, UY, MX, ES)
  const temas = [
    { key: "Sanitario", words: [
      "sanitario", "baño", "caño", "cañería", "agua", "inodoro", "lavatorio",
      "grifo", "llave de agua", "canilla", "ducha", "bañera", "bidet", "bidé",
      "desagüe", "desague", "cloaca", "filtración", "filtracion", "goteo", "gotera",
      "humedad", "pérdida", "perdida de agua", "tanque", "cisterna", "calefón", "calefon",
      "boiler", "termotanque", "plomería", "plomeria", "plomero"
    ]},
    { key: "Ascensor", words: [
      "ascensor", "ascensores", "elevador", "montacargas",
      "trabado", "atrapado", "freno", "parado", "no funciona el ascensor", "no anda el ascensor"
    ]},
    { key: "Electricidad", words: [
      "electricidad", "luz", "luces", "cortacircuito", "enchufe", "tablero",
      "cortocircuito", "fusible", "llave térmica", "termica", "disyuntor",
      "cable", "cableado", "apagón", "apagon", "sin luz", "foco", "lámpara", "lampara",
      "toma", "tomacorriente", "voltaje", "electricista", "interruptor"
    ]},
    { key: "Seguridad", words: [
      "seguridad", "cerradura", "portero", "camara", "cámara", "acceso",
      "llave", "puerta", "portón", "porton", "reja", "alarma", "intercomunicador",
      "portero eléctrico", "portero electrico", "videoportero", "timbre",
      "robo", "vandalismo", "roto", "forzado", "intruso", "candado"
    ]},
    { key: "Limpieza", words: [
      "limpieza", "basura", "olores", "olor",
      "sucio", "suciedad", "mugre", "manchas", "residuos",
      "contenedor", "tacho", "papelera", "barrer", "baldear", "fumigación", "fumigacion",
      "cucaracha", "plaga", "rata", "ratón", "raton", "insecto", "hormiga",
      "desinfección", "desinfeccion"
    ]},
    { key: "Estructural", words: [
      "estructura", "pared", "techo", "fisura", "grieta", "rajadura",
      "hundimiento", "derrumbe", "medianera", "revoque", "pintura",
      "membrana", "impermeabilización", "impermeabilizacion", "terraza",
      "balcón", "balcon", "baranda", "loseta", "baldosa", "azulejo", "mosaico"
    ]},
    { key: "Gas", words: [
      "gas", "olor a gas", "pérdida de gas", "perdida de gas",
      "caldera", "calefacción", "calefaccion", "estufa", "hornalla", "piloto"
    ]},
  ];
  for (const { key, words } of temas) {
    if (words.some((w) => lower.includes(w))) {
      result.issue = key;
      break;
    }
  }
  if (!result.issue) result.issue = "Otros";

  // Tipo: Edificio si menciona zonas comunes
  if (/\bedificio\b|\bcom[uú]n\b|\bzona\s+com[uú]n\b|\bascensor\b|\bentrada\b|\bhall\b|\bpasillo\b/.test(lower)) {
    result.type = "Edificio";
  }

  // Ubicación según contexto: dónde ocurre el problema dentro del edificio
  // 1) Contexto explícito "dentro de mi unidad" → Apartamento
  if (/\b(en\s+)?(mi\s+)?(apartamento|departamento|depto|unidad|casa)\b/.test(lower) ||
      /\bcocina\s+(de\s+mi|del\s+(apartamento|departamento|depto))\b/.test(lower) ||
      (/\b(en\s+la\s+)?cocina\b/.test(lower) && !/\b(sum|común|salón)\b/.test(lower)) ||
      (/\b(en\s+)?(mi\s+)?baño\b/.test(lower) && !/\b(edificio|común|pasillo)\b/.test(lower))) {
    result.ubicacion = "Apartamento";
  }
  // 2) Contexto zona común: "en la/el [lugar]" o "[lugar] del edificio"
  else if (/\b(en\s+la\s+)?cochera\b|\b(en\s+el\s+)?(garage|garaje)\b|\bportón\b/.test(lower)) {
    result.ubicacion = "Cochera";
  }
  else if (/\b(en\s+la\s+)?barbacoa\b|\b(en\s+el\s+)?quincho\b/.test(lower)) {
    result.ubicacion = "Barbacoa";
  }
  else if (/\b(en\s+el\s+)?patio\b/.test(lower)) {
    result.ubicacion = "Patio";
  }
  else if (/\b(en\s+la\s+)?(azotea|terraza)\b/.test(lower)) {
    result.ubicacion = "Azotea";
  }
  else if (/\b(en\s+(el|la)\s+)?(ascensor|ascensores)\b/.test(lower)) {
    result.ubicacion = "Ascensor";
  }
  else if (/\b(en\s+la\s+)?(entrada|puerta)\b|\bentrada\s+principal\b|\bpuerta\s+(de\s+)?ingreso\b|\b(en\s+)?recepción\b/.test(lower)) {
    result.ubicacion = "Puerta de ingreso";
  }
  else if (/\b(en\s+el\s+)?hall\b/.test(lower)) {
    result.ubicacion = "Hall";
  }
  else if (/\b(en\s+(el|la)\s+)?(pasillo|corredor)\b/.test(lower)) {
    const pisoMatch = lower.match(/(?:pasillo|corredor)\s*(?:del?\s+)?(?:segundo|tercer|primer|cuarto)\s+piso|(?:segundo|tercer|primer|cuarto)\s+piso|piso\s*(\d+)/);
    if (pisoMatch) {
      const num = pisoMatch[1] || (lower.includes("primer") ? "1" : lower.includes("segundo") ? "2" : lower.includes("tercer") ? "3" : "4");
      result.ubicacion = "Pasillo piso " + num;
    } else {
      result.ubicacion = "Pasillo";
    }
  }
  else if (/\b(en\s+(la|las)\s+)?escalera\b/.test(lower)) {
    result.ubicacion = "Escalera";
  }
  else if (/\b(en\s+)?(portería|recepción)\b/.test(lower)) {
    result.ubicacion = "Portería";
  }
  else if (/\b(en\s+la\s+)?(pileta|piscina)\b/.test(lower)) {
    result.ubicacion = "Pileta";
  }
  else if (/\b(sum|salón\s+de\s+usos\s+múltiples)\b/.test(lower)) {
    result.ubicacion = "SUM";
  }
  else if (/\b(en\s+el\s+)?(gimnasio|playroom)\b/.test(lower)) {
    result.ubicacion = "Gimnasio";
  }
  else if (/\b(baño\s+(del\s+)?edificio|baño\s+común|baño\s+del\s+pasillo)\b/.test(lower)) {
    result.ubicacion = "Baño común";
  }
  // 3) Palabra clave sin "en la/el" pero clara en contexto
  else if (/\b(planta\s+baja|primer\s+piso|segundo\s+piso|tercer\s+piso|cuarto\s+piso)\b/.test(lower)) {
    const m = lower.match(/(planta\s+baja|primer\s+piso|segundo\s+piso|tercer\s+piso|cuarto\s+piso)/);
    result.ubicacion = m ? m[1].charAt(0).toUpperCase() + m[1].slice(1) : "Apartamento";
  }
  else if (/\bcochera\b|\bgarage\b|\bgaraje\b/.test(lower)) {
    result.ubicacion = "Cochera";
  }
  else if (/\bapartamento\b|\bdepartamento\b|\bdepto\b|\bmi\s+unidad\b|\bcocina\b/.test(lower) && !/\b(sum|común)\b/.test(lower)) {
    result.ubicacion = "Apartamento";
  }
  else if (/\bpuerta\b|\bllave\b|\btrancada\b|\btrancado\b|\bafuera\b/.test(lower)) {
    result.ubicacion = "Puerta de ingreso";
  }
  // 4) Por tema si no hubo lugar claro
  else if (result.issue === "Ascensor") {
    result.ubicacion = "Ascensor";
  }
  else if (result.issue === "Sanitario") {
    result.ubicacion = "Área sanitaria";
  }
  else if (result.issue === "Electricidad" && !result.ubicacion) {
    result.ubicacion = "Área común";
  }
  else if (result.issue === "Seguridad" && !result.ubicacion) {
    result.ubicacion = "Accesos";
  }
  else if (result.issue === "Limpieza" && !result.ubicacion) {
    result.ubicacion = "Zona común";
  }

  // Título: primera oración o primeros 60 caracteres
  const firstSentence = t.split(/[.!?]/)[0]?.trim() || "";
  result.titulo = firstSentence.length > 0 && firstSentence.length <= 80
    ? firstSentence
    : t.slice(0, 60).trim() + (t.length > 60 ? "…" : "");
  if (result.titulo && result.titulo === result.descripcion.slice(0, result.titulo.length)) {
    result.descripcion = result.descripcion.slice(result.titulo.length).trim() || result.descripcion;
  }

  return result;
}

/**
 * H2 — Extrae los campos de una transcripción usando Claude Haiku 4.5.
 * Si la llamada al endpoint falla (timeout / red / 5xx), cae al regex
 * `extractIncidenciaFromTranscript` que estaba como única opción antes.
 * Esto garantiza que el flow de voz funcione aunque el backend esté caído.
 */
async function extractIncidenciaWithAI(transcript, buildingId) {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetch(`${HABI_API_BASE_URL}/api/extract-incidence-from-voice`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        transcript,
        building_id: buildingId ? String(buildingId) : null,
      }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    return {
      titulo: data.titulo || "",
      descripcion: data.descripcion || transcript,
      ubicacion: data.ubicacion || "",
      issue: data.issue || "",
      type: data.type || "Personal",
      prioridad: data.prioridad || "media",
      _source: "ai",
    };
  } catch (err) {
    console.warn("extractIncidenciaWithAI falló, fallback a regex:", err?.message || err);
    return { ...extractIncidenciaFromTranscript(transcript), _source: "regex" };
  }
}

// Estilo compartido de inputs/selects/textarea en la rama mobile (solo presentación).
const mobileFieldStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  border: `1px solid ${appColors.line}`,
  borderRadius: 14,
  background: appColors.surface,
  outline: "none",
  fontFamily: appType.fontFamily,
  fontSize: 14,
  fontWeight: 400,
  color: appColors.ink,
};

const Incidencias = ({ startVoice = false, onVoiceStarted, initialTab, setSelected, goBack }) => {
  // Modo demo: ocultamos el tab "Reportar" (write) y redireccionamos si lo intenta abrir.
  const { isDemo: isDemoUser } = useIsDemo();
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const { t } = useTranslation("incidencias");

  // Etiquetas localizadas para valores enumerados almacenados en Firestore
  // (tema/tipo/prioridad/estado). Sólo cambian el TEXTO mostrado; la lógica de
  // filtros y estilos sigue usando el valor crudo. Fallback al valor crudo si no
  // hay clave de traducción (datos inconsistentes o legacy).
  const themeLabel = (v) => (v ? t(`themes.${v}`, { defaultValue: v }) : v);
  const typeLabel = (v) => (v ? t(`types.${v}`, { defaultValue: v }) : v);
  const priorityLabel = (v) => {
    const k = (v || "").toLowerCase();
    return k ? t(`priorities.${k}`, { defaultValue: v }) : v;
  };
  const statusLabel = (v) => {
    const k = (v || "").toLowerCase().replace(/\s+/g, "_");
    return v ? t(`statuses.${k}`, { defaultValue: v }) : v;
  };
  const { selectedBuilding, selectedUnit } = useBuilding();
  const { email: effEmail, userid: effUserid, viewingAs } = useEffectiveIdentity();
  // "Ver como residente": modo solo lectura. Ocultamos "Reportar" (write) igual que en demo.
  const readOnly = useIsReadOnly();
  // userid de quien reporta para "Mis incidencias". En "ver como" sale del
  // contexto: quien mira es staff y con C10 no lista users por email.
  const useridDeQuienReporta = async () => {
    const ficha = await leerFichaResidente(db, {
      email: effEmail,
      userid: effUserid,
      viendoComo: readOnly,
      nombre: viewingAs?.name,
    });
    return ficha ? ficha.userid : null;
  };

  const [activeTab, setActiveTab] = useState(
    (isDemoUser || readOnly) ? "mis" : (initialTab || "reportar")
  );
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [ubicacion, setUbicacion] = useState("");
  const [prioridad, setPrioridad] = useState("media");
  const [imagen, setImagen] = useState(null);
  const [issue, setIssue] = useState("");
  const [type, setType] = useState("");
  const [misIncidencias, setMisIncidencias] = useState([]);
  const [incidenciasEdificio, setIncidenciasEdificio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detalleIncidencia, setDetalleIncidencia] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState("todas"); // "todas", "reportadas", "resueltas"
  const [filterProceso, setFilterProceso] = useState("todos"); // "todos", "en_proceso", "pendiente"
  const [filterPrioridad, setFilterPrioridad] = useState("todas"); // "todas", "alta", "media", "baja"
  const [filterTema, setFilterTema] = useState("todos"); // "todos", "Sanitario", "Ascensor", etc.
  const [filterFecha, setFilterFecha] = useState("todas"); // "todas", "hoy", "semana", "mes", "trimestre"
  const [isRecording, setIsRecording] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceStatus, setVoiceStatus] = useState("");
  const recognitionRef = useRef(null);
  const voiceListeningRef = useRef(false);
  const voiceTranscriptRef = useRef("");

  const startVoiceRecording = async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceStatus("unsupported");
      showToast(t("toast.voiceUnsupported"), "warning");
      return;
    }

    // Pedir permiso de micrófono explícitamente. Sin esto, en algunos browsers
    // SpeechRecognition.start() falla silenciosamente cuando no hay permiso.
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Cerramos el stream ya — solo lo abrimos para forzar el prompt de permiso.
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch (permErr) {
      console.warn("Permiso de micrófono denegado:", permErr);
      setVoiceStatus("permission_denied");
      showToast(t("toast.micBlocked"), "error");
      return;
    }

    setVoiceStatus("listening");
    setVoiceTranscript("");
    voiceTranscriptRef.current = "";
    voiceListeningRef.current = true;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = getLocaleTag();
    recognition.onresult = (event) => {
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) final += event.results[i][0].transcript + " ";
      }
      if (final) {
        voiceTranscriptRef.current = voiceTranscriptRef.current + final;
        setVoiceTranscript(voiceTranscriptRef.current);
      }
    };
    recognition.onerror = (event) => {
      const err = event?.error || "unknown";
      console.warn("SpeechRecognition error:", err, event);
      setVoiceStatus("error");
      if (err === "not-allowed" || err === "service-not-allowed") {
        showToast(t("toast.micBlockedBar"), "error");
      } else if (err === "no-speech") {
        showToast(t("toast.noSpeech"), "warning");
      } else if (err === "audio-capture") {
        showToast(t("toast.noMic"), "error");
      } else if (err === "network") {
        showToast(t("toast.noNetwork"), "error");
      } else {
        showToast(t("toast.recordingError", { err }), "error");
      }
    };
    recognition.onend = () => {
      if (voiceListeningRef.current) setVoiceStatus("done");
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsRecording(true);
    } catch (startErr) {
      console.error("recognition.start() failed:", startErr);
      setVoiceStatus("error");
      showToast(t("toast.recordingStartError"), "error");
    }
  };

  // Si se abrió desde el FAB (micrófono), ir a reportar y arrancar grabación
  useEffect(() => {
    if (!startVoice || readOnly) return;
    setActiveTab("reportar");
    const timer = setTimeout(() => {
      startVoiceRecording();
      onVoiceStarted?.();
    }, 400);
    return () => clearTimeout(timer);
  }, [startVoice]);

  const stopVoiceRecording = () => {
    voiceListeningRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
    setVoiceStatus("processing");
    // Dar tiempo a que llegue el último onresult antes de leer la transcripción
    setTimeout(async () => {
      const transcript = (voiceTranscriptRef.current || voiceTranscript || "").trim();
      if (transcript) {
        // H2 — usar AI (Claude Haiku) con fallback a regex.
        const extracted = await extractIncidenciaWithAI(
          transcript,
          selectedBuilding?.buildingid,
        );
        setTitulo(extracted.titulo);
        setDescripcion(extracted.descripcion);
        setUbicacion(extracted.ubicacion);
        setIssue(extracted.issue);
        setType(extracted.type);
        setPrioridad(extracted.prioridad);
      }
      setVoiceTranscript(voiceTranscriptRef.current || "");
      setVoiceStatus("done");
    }, 350);
  };

  const applyVoiceToForm = async () => {
    const transcript = voiceTranscript.trim();
    if (!transcript) return;
    // H2 — usar AI con fallback a regex.
    const extracted = await extractIncidenciaWithAI(
      transcript,
      selectedBuilding?.buildingid,
    );
    setTitulo(extracted.titulo);
    setDescripcion(extracted.descripcion);
    setUbicacion(extracted.ubicacion);
    setIssue(extracted.issue);
    setType(extracted.type);
    setPrioridad(extracted.prioridad);
    setVoiceStatus("");
    setVoiceTranscript("");
    voiceTranscriptRef.current = "";
  };

  const clearVoiceBlock = () => {
    setVoiceTranscript("");
    voiceTranscriptRef.current = "";
    setVoiceStatus("");
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
      recognitionRef.current = null;
    }
    setIsRecording(false);
  };

  useEffect(() => {
    if (!selectedBuilding || !effEmail) return;

    const fetchIncidencias = async () => {
      setLoading(true);
      try {
        const rawReported = await useridDeQuienReporta();
        if (rawReported == null) {
          console.error("Usuario no encontrado");
          return;
        }
        const buildingIn = tolerantId(selectedBuilding.buildingid);
        const reportedIn = tolerantId(rawReported);

        if (activeTab === "mis") {
          const q = query(
            collection(db, "incidents"),
            where("reportedby", "in", reportedIn),
            where("buildingid", "in", buildingIn),
            orderBy("date", "desc")
          );
          const snapshot = await getDocs(q);
          const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          
          setMisIncidencias(data);
        }

        if (activeTab === "edificio") {
          const q = query(
            collection(db, "incidents"),
            where("buildingid", "in", buildingIn),
            where("type", "==", "Edificio"),
            orderBy("date", "desc")
          );
          const snapshot = await getDocs(q);
          const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          
          devLog("Incidencias del edificio cargadas:", data);
          devLog("Query ejecutada para incidencias del edificio");

          setIncidenciasEdificio(data);
        }
      } catch (error) {
        console.error("Error al cargar incidencias:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchIncidencias();
    // useridDeQuienReporta lee effEmail, effUserid, readOnly y viewingAs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBuilding, effEmail, effUserid, readOnly, viewingAs?.name]);

  const getPrioridadStyle = (prioridad) => {
    switch (prioridad) {
      case "alta":
        return styles.priorityHigh;
      case "media":
        return styles.priorityMedium;
      case "baja":
        return styles.priorityLow;
      default:
        return styles.priorityMedium;
    }
  };

  const getEstadoStyle = (estado) => {
    const status = (estado || "").toLowerCase();
    switch (status) {
      case "pendiente":
        return styles.statusPending;
      case "resuelta":
      case "resuelto":
        return styles.statusResolved;
      case "en proceso":
      case "en curso":
        return styles.statusInProgress;
      case "reportada":
      case "reportado":
        return styles.statusReported;
      default:
        return styles.statusDefault;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) { showToast(t("toast.readOnly")); return; }
    if (!selectedBuilding) return;
    setSubmitting(true);

    try {
      const user = auth.currentUser;
      const userQuery = query(collection(db, "users"), where("email", "==", user.email));
      const userSnapshot = await getDocs(userQuery);
      if (userSnapshot.empty) {
        console.error("Usuario no encontrado");
        showToast(t("toast.userNotFound"), "error");
        return;
      }

      const userDoc = userSnapshot.docs[0];
      const userId = Number(userDoc.data().userid) || Number(userDoc.id);

      // Generar incidentid incremental de forma transaccional (evita condiciones de carrera)
      let incidentid = 1;
      const counterRef = doc(db, "counters", "incidents_global");
      await runTransaction(db, async (tx) => {
        const counterSnap = await tx.get(counterRef);
        if (!counterSnap.exists()) {
          tx.set(counterRef, { seq: 1 });
          incidentid = 1;
          return;
        }
        const current = Number(counterSnap.data()?.seq || 0);
        incidentid = current + 1;
        tx.update(counterRef, { seq: increment(1) });
      });

      let imageUrl = "";
      if (imagen) {
        const imageRef = ref(storage, `incidencias/${Date.now()}_${imagen.name}`);
        await uploadBytes(imageRef, imagen);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Capturar fecha y hora local correctamente
      const now = new Date();
      const fechaLocal = now.toLocaleDateString('es-UY', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split('/').reverse().join('-'); // Formato YYYY-MM-DD
      const horaLocal = now.toLocaleTimeString('es-UY', {
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });

      const nuevaIncidencia = {
        title: titulo,
        description: descripcion,
        ubication: ubicacion,
        date: fechaLocal,
        time: horaLocal,
        timestamp: now.getTime(), // Para ordenar correctamente
        status: "Reportada",
        state: "Reportada", // Mantener ambos para compatibilidad
        priority: prioridad,
        prioridad, // Mantener ambos para compatibilidad
        issue,
        type,
        reportedby: userId,
        userid: userId,
        buildingid: Number(selectedBuilding.buildingid),
        ...(selectedUnit && { unitId: selectedUnit.id }),
        imageUrl,
        incidentid: incidentid, // ID numérico único y autoincremental
      };

      const docRef = await addDoc(collection(db, "incidents"), nuevaIncidencia);

      // D1 — Disparar triage automático en background (no bloquear UI).
      // El triage clasifica tipo/prioridad/proveedor sugerido y se persiste
      // en `incidents.ai_triage`. El residente no ve nada de esto — es para
      // que el staff abra la incidencia y la encuentre pre-clasificada.
      (async () => {
        try {
          const headers = await getAuthHeaders();
          await fetch(`${HABI_API_BASE_URL}/api/triage-incident`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              incident_id: docRef.id,
              building_id: String(selectedBuilding.buildingid),
              force: false,
            }),
          });
        } catch (e) {
          console.warn("[D1] triage en background fallo (no critico):", e);
        }
      })();

      setSuccessMessage(t("form.success"));
      setTimeout(() => setSuccessMessage(""), 3000);

      // Limpiar formulario
      setTitulo("");
      setDescripcion("");
      setUbicacion("");
      setPrioridad("media");
      setImagen(null);
      setIssue("");
      setType("");

      // Recargar las incidencias
      setTimeout(() => {
        const fetchIncidencias = async () => {
          try {
            const rawReported = await useridDeQuienReporta();
            if (rawReported == null) return;
            const buildingIn = tolerantId(selectedBuilding.buildingid);
            const reportedIn = tolerantId(rawReported);

            // Recargar mis incidencias
            const misQuery = query(
              collection(db, "incidents"),
              where("reportedby", "in", reportedIn),
              where("buildingid", "in", buildingIn),
              orderBy("date", "desc")
            );
            const misSnapshot = await getDocs(misQuery);
            const misData = misSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setMisIncidencias(misData);

            // Recargar incidencias del edificio
            const edificioQuery = query(
              collection(db, "incidents"),
              where("buildingid", "in", buildingIn),
              where("type", "==", "Edificio"),
              orderBy("date", "desc")
            );
            const edificioSnapshot = await getDocs(edificioQuery);
            const edificioData = edificioSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setIncidenciasEdificio(edificioData);

            devLog("Incidencias recargadas después de crear nueva");
          } catch (error) {
            console.error("Error al recargar incidencias:", error);
          }
        };

        fetchIncidencias();
      }, 1000);
    } catch (error) {
      console.error("Error al registrar incidencia:", error);
      showToast(t("toast.submitError"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => formatDateLocalized(dateString);
  
  const formatDateTime = (inc) => {
    const fecha = formatDate(inc.date);
    if (inc.time) {
      return `${fecha} ${inc.time}`;
    }
    return fecha;
  };

  // Función para filtrar incidencias según todos los filtros seleccionados
  const getFilteredIncidencias = (data) => {
    let filtered = [...data];
    
    // Filtro por status (reportadas/resueltas)
    if (filterStatus === "reportadas") {
      filtered = filtered.filter(inc => {
        const status = (inc.status || inc.state || "").toLowerCase();
        return status === "reportada" || status === "reportado";
      });
    } else if (filterStatus === "resueltas") {
      filtered = filtered.filter(inc => {
        const status = (inc.status || inc.state || "").toLowerCase();
        return status === "resuelta" || status === "resuelto";
      });
    }
    
    // Filtro por proceso (en proceso, pendiente)
    if (filterProceso === "en_proceso") {
      filtered = filtered.filter(inc => {
        const status = (inc.status || inc.state || "").toLowerCase();
        return status === "en proceso" || status === "en curso";
      });
    } else if (filterProceso === "pendiente") {
      filtered = filtered.filter(inc => {
        const status = (inc.status || inc.state || "").toLowerCase();
        return status === "pendiente";
      });
    }
    
    // Filtro por prioridad
    if (filterPrioridad !== "todas") {
      filtered = filtered.filter(inc => {
        const priority = (inc.priority || inc.prioridad || "").toLowerCase();
        return priority === filterPrioridad;
      });
    }
    
    // Filtro por tema
    if (filterTema !== "todos") {
      filtered = filtered.filter(inc => inc.issue === filterTema);
    }
    
    // Filtro por fecha
    if (filterFecha !== "todas") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(inc => {
        const incDate = new Date(inc.date);
        const incDateOnly = new Date(incDate.getFullYear(), incDate.getMonth(), incDate.getDate());
        
        switch (filterFecha) {
          case "hoy":
            return incDateOnly.getTime() === today.getTime();
          case "semana": {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return incDateOnly >= weekAgo;
          }
          case "mes": {
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return incDateOnly >= monthAgo;
          }
          case "trimestre": {
            const threeMonthsAgo = new Date(today);
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            return incDateOnly >= threeMonthsAgo;
          }
          default:
            return true;
        }
      });
    }
    
    return filtered;
  };
  
  // Resetear todos los filtros
  const resetFilters = () => {
    setFilterStatus("todas");
    setFilterProceso("todos");
    setFilterPrioridad("todas");
    setFilterTema("todos");
    setFilterFecha("todas");
  };

  const renderIncidenciasList = (data, emptyMessage) => {
    const filteredData = getFilteredIncidencias(data);
    
    return (
      <div className={styles.incidenciasList}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Loader />
          </div>
        ) : filteredData.length === 0 ? (
          <div className={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h3 className={styles.emptyTitle}>
              {(filterStatus !== "todas" || filterProceso !== "todos" || filterPrioridad !== "todas" || filterTema !== "todos" || filterFecha !== "todas")
                ? t("empty.filteredTitle")
                : t("empty.noneTitle")
              }
            </h3>
            <p className={styles.emptyMessage}>
              {(filterStatus !== "todas" || filterProceso !== "todos" || filterPrioridad !== "todas" || filterTema !== "todos" || filterFecha !== "todas")
                ? t("empty.filteredMessage")
                : t("empty.noneMessage")
              }
            </p>
            {activeTab === "mis" && !readOnly && filterStatus === "todas" && filterProceso === "todos" && filterPrioridad === "todas" && filterTema === "todos" && filterFecha === "todas" && (
              <button
                className={styles.createFirstButton}
                onClick={() => setActiveTab("reportar")}
              >
                <FaClipboardList className={styles.buttonIconSvg} />
                {t("empty.createFirst")}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.incidenciasGrid}>
            {filteredData.map((inc) => (
              <div
                key={inc.id}
                className={styles.incidenciaCard}
                data-status={inc.status || inc.state}
                data-priority={inc.priority || inc.prioridad}
                onClick={() => setDetalleIncidencia(inc)}
              >
                <div className={styles.incidenciaHeader}>
                  <div className={styles.incidenciaTitleSection}>
                    <h3 className={styles.incidenciaTitle}>{inc.title}</h3>
                    <div className={styles.incidenciaType}>
                      <span className={styles.typeIcon}>
                        {inc.type === "Personal" ? <FaUser /> : <FaBuilding />}
                      </span>
                      <span className={styles.typeText}>{typeLabel(inc.type)}</span>
                    </div>
                  </div>
                  <div className={styles.incidenciaMeta}>
                    <span className={`${styles.statusBadge} ${getEstadoStyle(inc.status || inc.state)}`}>
                      {statusLabel(inc.status || inc.state)}
                    </span>
                    <span className={`${styles.priorityBadge} ${getPrioridadStyle(inc.priority || inc.prioridad)}`}>
                      {priorityLabel(inc.priority || inc.prioridad)}
                    </span>
                  </div>
                </div>
                
                <div className={styles.incidenciaContent}>
                  <p className={styles.incidenciaDescription}>
                    {inc.description.length > 120 
                      ? `${inc.description.substring(0, 120)}...` 
                      : inc.description
                    }
                  </p>
                  
                  <div className={styles.incidenciaDetails}>
                    <div className={styles.incidenciaDetail}>
                      <span className={styles.detailIcon}>•</span>
                      <span className={styles.detailLabel}>{t("list.themeLabel")}</span>
                      <span className={styles.detailValue}>{themeLabel(inc.issue)}</span>
                    </div>
                    {inc.ubication && (
                      <div className={styles.incidenciaDetail}>
                        <span className={styles.detailIcon}>•</span>
                        <span className={styles.detailLabel}>{t("list.locationLabel")}</span>
                        <span className={styles.detailValue}>{inc.ubication}</span>
                      </div>
                    )}
                  </div>
                  {(inc.lastClientResponse || inc.lastResponse) && (
                    <p
                      style={{
                        fontSize: 13,
                        color: "#065f46",
                        marginTop: 10,
                        paddingTop: 10,
                        borderTop: "1px solid #eef2f7",
                        lineHeight: 1.4,
                      }}
                    >
                      <strong style={{ display: "block", marginBottom: 4, fontSize: 11, color: "#64748b" }}>
                        {t("list.adminResponse")}
                      </strong>
                      {(inc.lastClientResponse || inc.lastResponse).length > 140
                        ? `${(inc.lastClientResponse || inc.lastResponse).slice(0, 140)}…`
                        : inc.lastClientResponse || inc.lastResponse}
                    </p>
                  )}
                </div>
                
                <div className={styles.incidenciaFooter}>
                  <div className={styles.incidenciaDateSection}>
                    <span className={styles.incidenciaDate}>{formatDateTime(inc)}</span>
                  </div>
                  <div className={styles.incidenciaActions}>
                    {inc.imageUrl && (
                      <span className={styles.hasImage} title={t("list.hasImageTitle")}>
                        <FaPaperclip />
                      </span>
                    )}
                    <span className={styles.viewDetails}>{t("list.viewDetails")}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!selectedBuilding) {
    if (isMobile) {
      return (
        <AppScreen header={<AppHeader title={t("screenTitle")} onBack={goBack} />}>
          <AppEmptyState
            icon={<FaBuilding />}
            title={t("noBuilding.titleMobile")}
            description={t("noBuilding.descriptionMobile")}
          />
        </AppScreen>
      );
    }
    return (
      <div className={styles.noBuilding}>
        <div className={styles.noBuildingIcon}>
          <FaBuilding />
        </div>
        <h2>{t("noBuilding.title")}</h2>
        <p>{t("noBuilding.description")}</p>
      </div>
    );
  }

  // ─── Versión mobile: estilo app "Radial" (Screen 06 · Incidencias) ───────────
  if (isMobile) {
    // Detalle: la propia pantalla de detalle maneja su layout y su "volver".
    if (detalleIncidencia) {
      return (
        <DetalleIncidencia
          incidencia={detalleIncidencia}
          onBack={() => setDetalleIncidencia(null)}
          setSelected={setSelected}
          goBack={goBack}
        />
      );
    }

    // Flujo "reportar": mismo formulario + bloque de voz (lógica intacta), sólo
    // reenmarcado en AppScreen. Se conservan las clases del CSS module.
    if (activeTab === "reportar") {
      return (
        <AppScreen
          header={
            <AppHeader
              title={t("mobileForm.title")}
              subtitle={t("mobileForm.subtitle")}
              onBack={() => { setActiveTab("mis"); }}
            />
          }
        >
          <p style={{ ...appType.bodySm, margin: "0 0 16px" }}>
            {t("form.intro")}
          </p>

          {/* Bloque de voz: solo el botón "Contanos tu problema" (lógica intacta) */}
          <div className={styles.voiceBlock} style={{ marginBottom: 16 }}>
            {voiceStatus === "unsupported" && (
              <p className={styles.voiceHint}>
                {t("voice.unsupported")}
              </p>
            )}
            {voiceStatus === "error" && (
              <p className={styles.voiceError}>{t("voice.error")}</p>
            )}
            {(voiceStatus === "" || voiceStatus === "idle" || voiceStatus === "listening" || (voiceStatus === "done" && !voiceTranscript)) && voiceStatus !== "unsupported" && (
              <div className={styles.voiceActions}>
                <button
                  type="button"
                  className={isRecording ? styles.voiceBtnMicRecording : styles.voiceBtnRecord}
                  onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                  title={isRecording ? t("voice.stopTitle") : t("voice.recordTitle")}
                >
                  <FaMicrophone className={isRecording ? styles.voiceMicIconRecording : undefined} />
                  <span>{isRecording ? t("voice.recording") : t("voice.record")}</span>
                </button>
              </div>
            )}
            {voiceStatus === "processing" && (
              <div className={styles.voicePulse}>
                <span className={styles.voiceSpinner} />
                <span>{t("voice.processing")}</span>
              </div>
            )}
            {voiceTranscript && (voiceStatus === "done" || voiceStatus === "listening") && (
              <div className={styles.voiceTranscript}>
                <p className={styles.voiceTranscriptLabel}>{t("voice.transcriptLabel")}</p>
                <p className={styles.voiceTranscriptText}>{voiceTranscript}</p>
                {voiceStatus === "done" && (
                  <div className={styles.voiceTranscriptActions}>
                    <button type="button" className={styles.voiceBtnClear} onClick={clearVoiceBlock}>
                      {t("voice.discard")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {successMessage && (
            <AppCard accent="blue" style={{ marginBottom: 16, borderLeftColor: appColors.green }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FaCheckCircle style={{ color: appColors.green, flexShrink: 0 }} />
                <span style={{ ...appType.body }}>{successMessage}</span>
              </div>
            </AppCard>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <AppCard>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ ...appType.bodySm, display: "block", marginBottom: 6 }}>{t("form.titleLabel")}</label>
                  <input
                    type="text"
                    placeholder={t("form.titlePlaceholder")}
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    required
                    style={mobileFieldStyle}
                  />
                </div>

                <div>
                  <label style={{ ...appType.bodySm, display: "block", marginBottom: 6 }}>{t("form.descriptionLabel")}</label>
                  <textarea
                    placeholder={t("form.descriptionPlaceholder")}
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    required
                    rows={4}
                    style={{ ...mobileFieldStyle, resize: "vertical", lineHeight: 1.45 }}
                  />
                </div>

                <div>
                  <label style={{ ...appType.bodySm, display: "block", marginBottom: 6 }}>{t("form.locationLabel")}</label>
                  <input
                    type="text"
                    placeholder={t("form.locationPlaceholderMobile")}
                    value={ubicacion}
                    onChange={(e) => setUbicacion(e.target.value)}
                    style={mobileFieldStyle}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <label style={{ ...appType.bodySm, display: "block", marginBottom: 6 }}>{t("form.themeLabel")}</label>
                    <select value={issue} onChange={(e) => setIssue(e.target.value)} required style={mobileFieldStyle}>
                      <option value="">{t("select.choose")}</option>
                      <option value="Sanitario">{t("themes.Sanitario")}</option>
                      <option value="Ascensor">{t("themes.Ascensor")}</option>
                      <option value="Electricidad">{t("themes.Electricidad")}</option>
                      <option value="Seguridad">{t("themes.Seguridad")}</option>
                      <option value="Limpieza">{t("themes.Limpieza")}</option>
                      <option value="Estructural">{t("themes.Estructural")}</option>
                      <option value="Gas">{t("themes.Gas")}</option>
                      <option value="Otros">{t("themes.Otros")}</option>
                    </select>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label style={{ ...appType.bodySm, display: "block", marginBottom: 6 }}>{t("form.typeLabel")}</label>
                    <select value={type} onChange={(e) => setType(e.target.value)} required style={mobileFieldStyle}>
                      <option value="">{t("select.choose")}</option>
                      <option value="Personal">{t("types.Personal")}</option>
                      <option value="Edificio">{t("types.Edificio")}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ ...appType.bodySm, display: "block", marginBottom: 6 }}>{t("form.priorityLabel")}</label>
                  <select value={prioridad} onChange={(e) => setPrioridad(e.target.value)} style={mobileFieldStyle}>
                    <option value="baja">{t("priorities.baja")}</option>
                    <option value="media">{t("priorities.media")}</option>
                    <option value="alta">{t("priorities.alta")}</option>
                  </select>
                </div>

                <div>
                  <label style={{ ...appType.bodySm, display: "block", marginBottom: 6 }}>{t("form.imageLabel")}</label>
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
                      if (!allowedTypes.includes(file.type)) {
                        showToast(t("imageOnly"), "error");
                        e.target.value = "";
                        return;
                      }
                      if (file.size > 10 * 1024 * 1024) {
                        showToast("La imagen no puede superar los 10MB.", "error");
                        e.target.value = "";
                        return;
                      }
                      setImagen(file);
                    }}
                    accept="image/*"
                    style={{ display: "none" }}
                    id="image-upload-mobile"
                  />
                  <label
                    htmlFor="image-upload-mobile"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "12px 14px",
                      border: `1px dashed ${appColors.line}`,
                      borderRadius: 14,
                      background: appColors.surface2,
                      color: appColors.ink2,
                      cursor: "pointer",
                      ...appType.body,
                    }}
                  >
                    <FaCamera style={{ color: appColors.blue, flexShrink: 0 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                      {imagen ? imagen.name : t("form.selectImage")}
                    </span>
                  </label>
                </div>
              </div>
            </AppCard>

            <AppButton type="submit" block disabled={submitting} icon={!submitting ? <FaPaperPlane size={13} /> : undefined}>
              {submitting ? t("form.submitting") : t("form.submit")}
            </AppButton>
          </form>
        </AppScreen>
      );
    }

    // Listado: "Mis incidencias" / "Del edificio".
    const listData = activeTab === "edificio" ? incidenciasEdificio : misIncidencias;
    const filteredData = getFilteredIncidencias(listData);
    const abiertasCount = listData.filter((inc) => {
      const s = (inc.status || inc.state || "").toLowerCase();
      return s !== "resuelta" && s !== "resuelto" && s !== "cerrada" && s !== "cerrado";
    }).length;

    const priorityTone = (p) => {
      const v = (p || "").toLowerCase();
      if (v === "alta") return "red";
      if (v === "baja") return "green";
      return "amber";
    };

    return (
      <AppScreen
        header={
          <AppHeader
            title={t("screenTitle")}
            subtitle={`${selectedBuilding?.name || "Edificio"} · ${abiertasCount} abierta${abiertasCount === 1 ? "" : "s"}`}
            onBack={goBack}
          />
        }
      >
        {!isDemoUser && !readOnly && (
          <>
            <AppButton
              block
              icon={<FaClipboardList size={13} />}
              onClick={() => { resetFilters(); setActiveTab("reportar"); }}
              style={{ marginBottom: 10 }}
            >
              Nueva incidencia
            </AppButton>
            <button
              type="button"
              onClick={() => { resetFilters(); setActiveTab("reportar"); startVoiceRecording(); }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                width: "100%",
                textAlign: "center",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "4px 0",
                marginBottom: appSpacing.section,
                ...appType.body,
                color: appColors.blue,
              }}
            >
              <FaMicrophone size={13} style={{ flexShrink: 0 }} />
              ¿Preferís hablar? Reportá con Habi
            </button>
          </>
        )}

        <SegmentedControl
          value={activeTab}
          onChange={(v) => { setDetalleIncidencia(null); resetFilters(); setActiveTab(v); }}
          options={[
            { value: "mis", label: "Mis incidencias" },
            { value: "edificio", label: "Del edificio" },
          ]}
          style={{ marginBottom: appSpacing.section }}
        />

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <Loader />
          </div>
        ) : filteredData.length === 0 ? (
          <AppEmptyState
            icon={<FaTools />}
            title={activeTab === "mis" ? "No reportaste incidencias" : "Sin incidencias del edificio"}
            description={
              activeTab === "mis"
                ? "Cuando reportes un problema, vas a poder seguirlo desde acá."
                : "No hay incidencias de tipo 'Edificio' reportadas todavía."
            }
          />
        ) : (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>
              {activeTab === "mis" ? "Mis reportes" : "Del edificio"} ·{" "}
              {filteredData.length} en total
            </SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filteredData.map((inc) => {
                const estado = inc.status || inc.state || "";
                const estadoLower = estado.toLowerCase();
                const isResuelta = estadoLower === "resuelta" || estadoLower === "resuelto";
                const respuesta = inc.lastClientResponse || inc.lastResponse;
                const prioridadRaw = inc.priority || inc.prioridad || "";
                const pTone = priorityTone(prioridadRaw);
                // Color del riel/micro-label: verde si resuelta, si no por prioridad.
                const railColor = isResuelta
                  ? appColors.green
                  : pTone === "red"
                  ? appColors.red
                  : pTone === "green"
                  ? appColors.green
                  : pTone === "amber"
                  ? appColors.amber
                  : appColors.blue;
                // Icono de categoría (solo presentación, derivado de inc.issue).
                const IssueIcon = ISSUE_ICONS[inc.issue] || FaTools;
                // Unidad a mostrar en el footer (dato ya presente, sin nuevas queries).
                const unidadLabel =
                  inc.unitNumber != null
                    ? `Unidad ${inc.unitNumber}`
                    : inc.ubication || (inc.type === "Edificio" ? "Área común" : "Tu unidad");
                return (
                  <AppCard
                    key={inc.id}
                    accent="blue"
                    onClick={() => setDetalleIncidencia(inc)}
                    style={{ borderLeftColor: railColor }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <IconBox
                        icon={<IssueIcon />}
                        color={railColor}
                        size={40}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ ...appType.statLabel, color: railColor, display: "block", marginBottom: 3 }}>
                          {(estado || "Reportada").toUpperCase()}
                        </span>
                        <span style={{ ...appType.cardTitle, fontSize: 15, display: "block", overflowWrap: "anywhere" }}>
                          {inc.title}
                        </span>
                      </div>
                      <FaChevronRight style={{ color: appColors.ink3, fontSize: 13, flexShrink: 0, marginTop: 3 }} />
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "12px 0 0" }}>
                      {estado && (
                        <Badge tone={isResuelta ? "green" : "blue"} dot={isResuelta}>
                          {estado}
                        </Badge>
                      )}
                      {prioridadRaw && (
                        <Badge tone={pTone} dot>
                          {prioridadRaw}
                        </Badge>
                      )}
                      {inc.issue && <Badge tone="ink3">{inc.issue}</Badge>}
                      {inc.imageUrl && (
                        <Badge tone="ink3">
                          <FaPaperclip size={10} /> Foto
                        </Badge>
                      )}
                    </div>

                    {inc.description && (
                      <p style={{ ...appType.bodySm, margin: "12px 0 0", lineHeight: 1.45, overflowWrap: "anywhere" }}>
                        {inc.description.length > 140 ? `${inc.description.slice(0, 140)}…` : inc.description}
                      </p>
                    )}

                    {/* Footer estructurado en grilla: FECHA / UNIDAD */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 10,
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: `1px solid ${appColors.line2}`,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <span style={{ ...appType.statLabel, color: appColors.ink3, display: "block", marginBottom: 2 }}>
                          Fecha
                        </span>
                        <span style={{ ...appType.bodySm, color: appColors.ink }}>
                          {formatDateTime(inc)}
                        </span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <span style={{ ...appType.statLabel, color: appColors.ink3, display: "block", marginBottom: 2 }}>
                          {inc.type === "Edificio" ? "Ubicación" : "Unidad"}
                        </span>
                        <span style={{ ...appType.bodySm, color: appColors.ink, overflowWrap: "anywhere" }}>
                          {unidadLabel}
                        </span>
                      </div>
                    </div>

                    {respuesta && (
                      <div
                        style={{
                          marginTop: 12,
                          background: appColors.cream,
                          borderRadius: 14,
                          padding: "12px 14px",
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                        }}
                      >
                        <FaRegCommentDots
                          size={14}
                          style={{ color: appColors.blue, flexShrink: 0, marginTop: 2 }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <span style={{ ...appType.statLabel, color: appColors.blue, display: "block", marginBottom: 3 }}>
                            Respuesta de administración
                          </span>
                          <span style={{ ...appType.bodySm, color: appColors.ink, overflowWrap: "anywhere" }}>
                            {respuesta.length > 140 ? `${respuesta.slice(0, 140)}…` : respuesta}
                          </span>
                        </div>
                      </div>
                    )}
                  </AppCard>
                );
              })}
            </div>
          </>
        )}
      </AppScreen>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClipboardList />
        </div>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{t("header.title")}</h1>
          <p className={styles.subtitle}>
            {t("header.subtitle")}
          </p>
        </div>
      </div>

      {/* Tabs (en modo demo ocultamos "Reportar" porque es write) */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          {[
            ...((isDemoUser || readOnly) ? [] : [{ id: "reportar", label: t("tabs.report"), icon: <FaClipboardList /> }]),
            { id: "mis", label: t("tabs.mine"), icon: <FaUser /> },
            { id: "edificio", label: t("tabs.building"), icon: <FaBuilding /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setDetalleIncidencia(null);
                resetFilters();
                setActiveTab(tab.id);
              }}
              className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {activeTab === "reportar" && (
          <div className={styles.reportSection}>
            <div className={styles.reportCard}>
              <div className={styles.reportHeader}>
                <h2 className={styles.reportTitle}>{t("report.title")}</h2>
                <p className={styles.reportDescription}>
                  {t("report.description")}
                </p>
              </div>

              {/* Bloque de voz: solo el botón "Contanos tu problema" */}
              <div className={styles.voiceBlock}>
                {voiceStatus === "unsupported" && (
                  <p className={styles.voiceHint}>
                    {t("voice.unsupported")}
                  </p>
                )}
                {voiceStatus === "error" && (
                  <p className={styles.voiceError}>{t("voice.error")}</p>
                )}
                {(voiceStatus === "" || voiceStatus === "idle" || voiceStatus === "listening" || (voiceStatus === "done" && !voiceTranscript)) && voiceStatus !== "unsupported" && (
                  <div className={styles.voiceActions}>
                    <button
                      type="button"
                      className={isRecording ? styles.voiceBtnMicRecording : styles.voiceBtnRecord}
                      onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                      title={isRecording ? t("voice.stopTitle") : t("voice.recordTitle")}
                    >
                      <FaMicrophone className={isRecording ? styles.voiceMicIconRecording : undefined} />
                      <span>{isRecording ? t("voice.recording") : t("voice.record")}</span>
                    </button>
                  </div>
                )}
                {voiceStatus === "processing" && (
                  <div className={styles.voicePulse}>
                    <span className={styles.voiceSpinner} />
                    <span>{t("voice.processing")}</span>
                  </div>
                )}
                {voiceTranscript && (voiceStatus === "done" || voiceStatus === "listening") && (
                  <div className={styles.voiceTranscript}>
                    <p className={styles.voiceTranscriptLabel}>{t("voice.transcriptLabel")}</p>
                    <p className={styles.voiceTranscriptText}>{voiceTranscript}</p>
                    {voiceStatus === "done" && (
                      <div className={styles.voiceTranscriptActions}>
                        <button type="button" className={styles.voiceBtnClear} onClick={clearVoiceBlock}>
                          {t("voice.discard")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.formSectionLabel}>{t("formSectionLabel")}</div>

              {successMessage && (
                <div className={styles.successMessage}>
                  <FaCheckCircle className={styles.successIcon} />
                  {successMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className={styles.reportForm}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("form.titleLabel")}</label>
                    <input
                      type="text"
                      placeholder={t("form.titlePlaceholder")}
                      value={titulo}
                      onChange={(e) => setTitulo(e.target.value)}
                      required
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("form.descriptionLabel")}</label>
                    <textarea
                      placeholder={t("form.descriptionPlaceholder")}
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      required
                      rows={3}
                      className={styles.textarea}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("form.locationLabel")}</label>
                    <input
                      type="text"
                      placeholder={t("form.locationPlaceholder")}
                      value={ubicacion}
                      onChange={(e) => setUbicacion(e.target.value)}
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("form.themeLabel")}</label>
                    <select
                      value={issue}
                      onChange={(e) => setIssue(e.target.value)}
                      required
                      className={styles.select}
                    >
                      <option value="">{t("select.chooseTheme")}</option>
                      <option value="Sanitario">{t("themes.Sanitario")}</option>
                      <option value="Ascensor">{t("themes.Ascensor")}</option>
                      <option value="Electricidad">{t("themes.Electricidad")}</option>
                      <option value="Seguridad">{t("themes.Seguridad")}</option>
                      <option value="Limpieza">{t("themes.Limpieza")}</option>
                      <option value="Estructural">{t("themes.Estructural")}</option>
                      <option value="Gas">{t("themes.Gas")}</option>
                      <option value="Otros">{t("themes.Otros")}</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("form.typeLabel")}</label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      required
                      className={styles.select}
                    >
                      <option value="">{t("select.chooseType")}</option>
                      <option value="Personal">{t("types.Personal")}</option>
                      <option value="Edificio">{t("types.Edificio")}</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("form.priorityLabel")}</label>
                    <select
                      value={prioridad}
                      onChange={(e) => setPrioridad(e.target.value)}
                      className={`${styles.select} ${styles.prioritySelect} ${styles[`priority${prioridad.charAt(0).toUpperCase() + prioridad.slice(1)}Select`]}`}
                    >
                      <option value="baja">{t("priorities.baja")}</option>
                      <option value="media">{t("priorities.media")}</option>
                      <option value="alta">{t("priorities.alta")}</option>
                    </select>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("form.imageLabel")}</label>
                    <div className={styles.fileUpload}>
                      <input
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
                          if (!allowedTypes.includes(file.type)) {
                            showToast(t("imageOnly"), "error");
                            e.target.value = "";
                            return;
                          }
                          if (file.size > 10 * 1024 * 1024) {
                            showToast("La imagen no puede superar los 10MB.", "error");
                            e.target.value = "";
                            return;
                          }
                          setImagen(file);
                        }}
                        accept="image/*"
                        className={styles.fileInput}
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" className={styles.fileLabel}>
                        <FaCamera className={styles.fileIconSvg} />
                        {imagen ? imagen.name : t("form.selectImage")}
                      </label>
                    </div>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <button 
                    type="submit" 
                    className={styles.submitButton}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className={styles.buttonSpinner}></span>
                        {t("form.submitting")}
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className={styles.buttonIconSvg} />
                        {t("form.submit")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {(activeTab === "mis" || activeTab === "edificio") && (
          detalleIncidencia ? (
            <DetalleIncidencia
              incidencia={detalleIncidencia}
              onBack={() => setDetalleIncidencia(null)}
            />
          ) : (
            <div className={styles.listSection}>
              <div className={styles.listHeader}>
                <div className={styles.listHeaderTop}>
                  <div className={styles.listTitleSection}>
                    <div className={styles.listIcon}>
                      {activeTab === "mis" ? <FaUser /> : <FaBuilding />}
                    </div>
                    <div className={styles.listTitleContent}>
                      <h2 className={styles.listTitle}>
                        {activeTab === "mis" ? "Mis Incidencias" : "Incidencias del Edificio"}
                      </h2>
                      <p className={styles.listSubtitle}>
                        {activeTab === "mis" 
                          ? "Todas las incidencias que has reportado personalmente"
                          : "Incidencias de tipo 'Edificio' reportadas por todos los residentes"
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className={styles.filtersSection}>
                  <div className={styles.filtersBar}>
                    {/* Stats a la izquierda */}
                    <div className={styles.listStats}>
                      <div 
                        className={`${styles.statItem} ${styles.statClickable} ${filterStatus === "todas" ? styles.statActive : ""}`}
                        onClick={() => setFilterStatus("todas")}
                      >
                        <span className={styles.statNumber}>
                          {activeTab === "mis" ? misIncidencias.length : incidenciasEdificio.length}
                        </span>
                        <span className={styles.statLabel}>Total</span>
                      </div>
                      <div 
                        className={`${styles.statItem} ${styles.statClickable} ${filterStatus === "reportadas" ? styles.statActive : ""}`}
                        onClick={() => setFilterStatus("reportadas")}
                      >
                        <span className={styles.statNumber}>
                          {activeTab === "mis" 
                            ? misIncidencias.filter(inc => (inc.status || inc.state) === "Reportada" || (inc.status || inc.state) === "Reportado").length
                            : incidenciasEdificio.filter(inc => (inc.status || inc.state) === "Reportada" || (inc.status || inc.state) === "Reportado").length
                          }
                        </span>
                        <span className={styles.statLabel}>Reportadas</span>
                      </div>
                      <div 
                        className={`${styles.statItem} ${styles.statClickable} ${filterStatus === "resueltas" ? styles.statActive : ""}`}
                        onClick={() => setFilterStatus("resueltas")}
                      >
                        <span className={styles.statNumber}>
                          {activeTab === "mis" 
                            ? misIncidencias.filter(inc => (inc.status || inc.state) === "Resuelta" || (inc.status || inc.state) === "Resuelto").length
                            : incidenciasEdificio.filter(inc => (inc.status || inc.state) === "Resuelta" || (inc.status || inc.state) === "Resuelto").length
                          }
                        </span>
                        <span className={styles.statLabel}>Resueltas</span>
                      </div>
                    </div>
                    
                    {/* Filtros a la derecha */}
                    <div className={styles.filtersRow}>
                      <select 
                        value={filterFecha} 
                        onChange={(e) => setFilterFecha(e.target.value)}
                        className={`${styles.filterSelect} ${filterFecha !== "todas" ? styles.filterSelectActive : ""}`}
                      >
                        <option value="todas">{t("filters.date")}</option>
                        <option value="hoy">{t("filters.today")}</option>
                        <option value="semana">{t("filters.week")}</option>
                        <option value="mes">{t("filters.month")}</option>
                        <option value="trimestre">{t("filters.quarter")}</option>
                      </select>
                      
                      <select 
                        value={filterProceso} 
                        onChange={(e) => setFilterProceso(e.target.value)}
                        className={`${styles.filterSelect} ${filterProceso !== "todos" ? styles.filterSelectActive : ""}`}
                      >
                        <option value="todos">{t("filters.status")}</option>
                        <option value="en_proceso">{t("filters.inProcess")}</option>
                        <option value="pendiente">{t("filters.pending")}</option>
                      </select>
                      
                      <select 
                        value={filterPrioridad} 
                        onChange={(e) => setFilterPrioridad(e.target.value)}
                        className={`${styles.filterSelect} ${filterPrioridad !== "todas" ? styles.filterSelectActive : ""}`}
                      >
                        <option value="todas">{t("filters.priority")}</option>
                        <option value="alta">{t("priorities.alta")}</option>
                        <option value="media">{t("priorities.media")}</option>
                        <option value="baja">{t("priorities.baja")}</option>
                      </select>
                      
                      <select 
                        value={filterTema} 
                        onChange={(e) => setFilterTema(e.target.value)}
                        className={`${styles.filterSelect} ${filterTema !== "todos" ? styles.filterSelectActive : ""}`}
                      >
                        <option value="todos">{t("filters.theme")}</option>
                        <option value="Sanitario">{t("themes.Sanitario")}</option>
                        <option value="Ascensor">{t("themes.Ascensor")}</option>
                        <option value="Electricidad">{t("themes.Electricidad")}</option>
                        <option value="Seguridad">{t("themes.Seguridad")}</option>
                        <option value="Limpieza">{t("themes.Limpieza")}</option>
                        <option value="Estructural">{t("themes.Estructural")}</option>
                        <option value="Gas">{t("themes.Gas")}</option>
                        <option value="Otros">{t("themes.Otros")}</option>
                      </select>
                      
                      {(filterProceso !== "todos" || filterPrioridad !== "todas" || filterTema !== "todos" || filterStatus !== "todas" || filterFecha !== "todas") && (
                        <button 
                          className={styles.clearFiltersBtn}
                          onClick={resetFilters}
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {renderIncidenciasList(
                activeTab === "mis" ? misIncidencias : incidenciasEdificio,
                activeTab === "mis" 
                  ? "No has reportado ninguna incidencia aún. Usa el tab 'Reportar Incidencia' para crear tu primera incidencia."
                  : "No hay incidencias de tipo 'Edificio' reportadas en este edificio."
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Incidencias;